<template>
  <Layout :showHeader="false">
    <template #default>
      <div class="row q-col-gutter-x-md full-height" >
        <div class="col-12 col-xs-12 col-sm-12 column justify-around">
          <q-card dark bordered class="full-height column justify-center">
            <q-card-section class="overflow-hidden">
              <div class="main-content">
                <div class="main-title">智能底盘</div>
                <div class="sub-items-container">
                  <div class="sub-item-emb sub-item">EMB</div>
                  <div class="sub-item-aws-1 sub-item">SBW</div>
                  <div class="sub-item-aws-2 sub-item">RWS</div>
                  <div class="sub-item-aws-3 sub-item">4WD</div>
                </div>
              </div>
              <div class="circle-container">
                <div class="circle-bottom">
                  <div class="circle-bottom-text">伏羲</div>
                </div>
                <div ref="circleTop" class="circle-top">
                  <div
                    v-for="(tabItem, index) in menuLists"
                    :key="index"
                    :name="tabItem.name"
                    :to="tabItem.value"
                    :label="tabItem.name"
                    :class="`sub-item-${tabItem.name}`"
                    @click="navigateTo(tabItem.name)"
                    style="cursor: pointer"
                  >
                    {{ tabItem.name }}
                  </div>
                </div>
                <div ref="circleTopItem" class="circle-top-item"></div>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </template>
  </Layout>
</template>

<script setup lang="ts">
import Layout from "./components/Layout.vue";
import { ref, onMounted, onBeforeUnmount } from "vue";
import { menuList } from "./helper";
import { useRouter } from "vue-router";
const enlargedItem = ref(); // 新增变量存储当前放大的元素
const router = useRouter();
const menuLists = ref(menuList.filter((item) => item.name !== "HOME"));
function navigateTo(path: string) {
  menuLists.value.forEach((item) => {
    if (item.name === path) {
      router.push(item.value);
    }
  });
}

const circleTop = ref<HTMLElement | null>(null);
const circleTopItem = ref<HTMLElement | null>(null);
let isDragging = false;
let startX = 0;
let startY = 0;
let currentAngle = 0;
let lastUpdateTime = 0;
let animationFrameId: number | null = null;
let lastDx = 0;
let momentum = 0;
let isAnimating = false;

// 优化旋转配置
const rotationConfig = {
  sensitivity: 0.0005, // 增加灵敏度
  direction: -1,
  speed: 5, // 降低速度以提高控制精度
  momentumDecay: 0.95, // 动量衰减系数
  minUpdateInterval: 16, // 约60fps的更新频率
};

onMounted(() => {
  positionItems();
  if (circleTop.value) {
    circleTop.value.addEventListener("mousedown", handleMouseDown, { passive: true });
    circleTop.value.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("mousemove", handleMouseMove, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchend", handleTouchEnd);
  }
});

onBeforeUnmount(() => {
  // 清理事件监听器和动画帧
  if (circleTop.value) {
    circleTop.value.removeEventListener("mousedown", handleMouseDown);
    circleTop.value.removeEventListener("touchstart", handleTouchStart);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.removeEventListener("touchend", handleTouchEnd);
  }
  
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }
});

// 处理鼠标按下事件
function handleMouseDown(event: MouseEvent) {
  isDragging = true;
  startX = event.clientX;
  startY = event.clientY;
  lastDx = 0;
  momentum = 0;
  stopMomentumAnimation();
}

// 处理触摸开始事件
function handleTouchStart(event: TouchEvent) {
  if (event.touches.length === 1) {
    isDragging = true;
    const touch = event.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    lastDx = 0;
    momentum = 0;
    stopMomentumAnimation();
  }
}

// 处理鼠标移动事件
function handleMouseMove(event: MouseEvent) {
  if (!isDragging) return;
  
  const now = performance.now();
  if (now - lastUpdateTime < rotationConfig.minUpdateInterval) return;
  
  const dx = event.clientX - startX;
  const dy = event.clientY - startY;
  
  // 计算动量
  momentum = dx - lastDx;
  lastDx = dx;
  
  updateAngle(dx, dy);
  lastUpdateTime = now;
  
  // 更新起始位置以实现连续滑动
  startX = event.clientX;
  startY = event.clientY;
}

// 处理触摸移动事件
function handleTouchMove(event: TouchEvent) {
  if (!isDragging || event.touches.length !== 1) return;
  
  const now = performance.now();
  if (now - lastUpdateTime < rotationConfig.minUpdateInterval) return;
  
  const touch = event.touches[0];
  const dx = touch.clientX - startX;
  const dy = touch.clientY - startY;
  
  // 计算动量
  momentum = dx - lastDx;
  lastDx = dx;
  
  updateAngle(dx, dy);
  lastUpdateTime = now;
  
  // 更新起始位置以实现连续滑动
  startX = touch.clientX;
  startY = touch.clientY;
}

// 处理鼠标抬起事件
function handleMouseUp() {
  if (isDragging) {
    isDragging = false;
    startMomentumAnimation();
  }
}

// 处理触摸结束事件
function handleTouchEnd() {
  if (isDragging) {
    isDragging = false;
    startMomentumAnimation();
  }
}

// 启动动量动画
function startMomentumAnimation() {
  if (Math.abs(momentum) > 0.5 && !isAnimating) {
    isAnimating = true;
    animateMomentum();
  }
}

// 停止动量动画
function stopMomentumAnimation() {
  isAnimating = false;
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

// 动量动画
function animateMomentum() {
  if (!isAnimating) return;
  
  momentum *= rotationConfig.momentumDecay;
  
  if (Math.abs(momentum) < 0.5) {
    stopMomentumAnimation();
    return;
  }
  
  updateAngle(momentum, 0);
  animationFrameId = requestAnimationFrame(animateMomentum);
}

// 根据鼠标或触摸移动的距离更新旋转角度
function updateAngle(dx: number, dy: number) {
  currentAngle +=
    dx *
    rotationConfig.sensitivity *
    rotationConfig.direction *
    rotationConfig.speed;
  
  // 使用requestAnimationFrame来优化渲染
  if (animationFrameId === null) {
    animationFrameId = requestAnimationFrame(() => {
      positionItems(currentAngle);
      animationFrameId = null;
    });
  }
}

function positionItems(angle = 0) {
  const container = circleTop.value;
  const itemContainer = circleTopItem.value;
  if (!container || !itemContainer) return;

  const itemCount = menuLists.value.length;
  const radiusX = itemCount <= 8 ? 380 : 420;
  const radiusY = itemCount <= 8 ? 100 : 120;

  // 获取 circle-top-item 的边界
  const itemContainerRect = itemContainer.getBoundingClientRect();
  enlargedItem.value = ""; // 清空当前放大的元素
  const buffer = itemCount <= 8 ? 0 : 20; // 根据项目数量调整缓冲区
  
  for (let i = 0; i < itemCount; i++) {
    const itemAngle = (i / itemCount) * Math.PI * 2 + angle;
    const x = radiusX * Math.cos(itemAngle);
    const y = radiusY * Math.sin(itemAngle);
    const item = container.children[i] as HTMLElement;
    if (item) {
      // 使用transform3d启用硬件加速
      item.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      
      // 检查项目是否在 circle-top-item 区域内
      const itemRect = item.getBoundingClientRect();
      const isInCircleTopItem =
        itemRect.left >= itemContainerRect.left - buffer &&
        itemRect.right <= itemContainerRect.right + buffer &&
        itemRect.top >= itemContainerRect.top - buffer &&
        itemRect.bottom <= itemContainerRect.bottom + buffer;

      if (isInCircleTopItem) {
        item.style.fontSize = "36px"; // 放大字体
        item.style.color = "#000";
        enlargedItem.value = item.getAttribute("name"); // 更新当前放大的元素
      } else {
        item.style.fontSize = ""; // 恢复默认字体
        item.style.color = "";
      }
    }
  }
}
</script>
<style scoped lang="scss">
// 去除顶部 padding
:deep(.layout-content) {
  padding-top: 0 !important;
}
.main-content {
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  color: #000;
}
.main-title {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  font-weight: bold;
  display: flex;
  font-size: 28px;
  justify-content: center;
  align-items: center;
  box-shadow: inset 0 0 25px 35px rgba(255, 255, 255, 0.4);
  background: radial-gradient(
    rgb(168, 168, 168),
    rgb(186, 193, 202),
    rgb(245, 245, 245)
  );
  position: relative;
  z-index: 2;
  will-change: transform;
}
.sub-items-container {
  position: absolute;
  width: 270px;
  height: 270px;
  border-radius: 50%;
  display: flex;
  justify-content: space-around;
  align-items: space-around;
  flex-wrap: wrap;
  z-index: 3;
}
.sub-item {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  color: rgb(72, 72, 73);
  justify-content: center;
  align-items: center;
  font-size: 20px;
  box-shadow: inset 0 0 25px 35px rgba(255, 255, 255, 0.4);
  margin: 5px;
  font-weight: bold;
  z-index: 2;
  background: radial-gradient(
    rgb(154, 154, 155),
    rgb(186, 193, 202),
    rgb(245, 245, 245)
  );
  transition: font-size 0.2s ease, color 0.2s ease;
  will-change: transform, font-size, color;
}
/* 具体的子项 - EMB */
.sub-item-emb {
  position: absolute;
  top: 50%;
  left: -40%;
}

.sub-item-aws-1 {
  position: absolute;
  top: 50%;
  right: -40%;
}

.sub-item-aws-2 {
  position: absolute;
  top: 90%;
  left: 0%;
  z-index: 4;
}
.sub-item-aws-3 {
  position: absolute;
  top: 90%;
  right: 0%;
  z-index: 4;
}
.circle-container {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 300px;
  top: 30px;
  touch-action: none; /* 防止浏览器默认的触摸行为 */
}
.circle-top {
  width: 90%;
  height: 300px;
  border-radius: 50%;
  box-shadow: inset 0 0 25px 35px rgba(255, 255, 255, 0.4);
  background: radial-gradient(
    rgb(125, 125, 126),
    rgb(186, 193, 202),
    rgb(245, 245, 245)
  );
  position: relative;
  bottom: 30px;
  margin: 50px auto;
  will-change: transform;
  -webkit-backface-visibility: hidden; /* 优化移动端性能 */
  backface-visibility: hidden;
}
.circle-bottom-text {
  position: absolute;
  bottom: 70px;
  left: 43%;
  font-size: 46px;
  color: #fff;
  font-weight: bold;
}
.circle-bottom {
  color: rgb(72, 72, 73);
  width: 59%;
  height: 190px;
  border-radius: 50%;
  box-shadow: inset 0 0 25px 35px rgba(255, 255, 255, 0.4);
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    rgb(125, 125, 126),
    rgb(186, 193, 202),
    rgb(245, 245, 245)
  );
  z-index: 1;
  will-change: transform;
}
.sub-item-MAABS,
.sub-item-HDC,
.sub-item-IVSE,
.sub-item-ICC,
.sub-item-dTCS,
.sub-item-DST,
.sub-item-TAA,
.sub-item-CST,
.sub-item-IFTF,
.sub-item-EMB {
  position: absolute;
  top: 40%;
  left: 44%;
  width: 120px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  transform-origin: 0 0; /* 设置旋转中心 */
  color: rgb(72, 72, 73);
  font-weight: bold;
  will-change: transform, font-size, color;
}
.circle-top-item {
  width: 50%;
  height: 150px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 120px;
  z-index: -10;
}
</style>
